/**
 * The URL state every filterable listing shares (TRA-1397): search with a
 * debounce, the time range, the sort, the page and the metadata dropdowns.
 * The Trajectories page and the Requests view add their own params on
 * top (flag status, tags, HTTP status, fallback) through `updateParams`,
 * which resets the page the same way.
 *
 * Search is component state debounced into the query: the box keeps the
 * text as typed (a trailing space included) while the URL holds it
 * trimmed, and the query waits for the typing to stop. When navigation
 * changes `q` behind the box (the back button, a link to this page), the
 * box takes the URL's text (`searchAfterUrlChange`). A pasted run id or
 * trace key names one request of any age, so that search covers all time
 * and the range picker is held while it is in the box (`rangeForSearch`).
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useDebounce } from '@/hooks/useDebounce'

import {
  ANY,
  changedParams,
  isIdSearch,
  nextSort,
  parsePage,
  parseRange,
  parseSort,
  rangeForSearch,
  searchAfterUrlChange,
  sortParamValue,
  type MetadataParam,
  type SortState,
} from './listingParams'

export interface ListingConfig<R extends string, F extends string> {
  ranges: readonly R[]
  defaultRange: R
  sortFields: readonly F[]
  defaultSort: SortState<F>
  /** The metadata dropdowns this listing offers; `clearMetadata` drops them. */
  metadataParams: readonly MetadataParam[]
}

export interface ListingParams<R extends string, F extends string> {
  /** What the search box shows. */
  search: string
  /** What the query sends: trimmed and debounced. */
  debouncedSearch: string
  /** The debounced search is a run id or trace key, so the range is held
      at all time. */
  idSearch: boolean
  /** The picked range, from the URL. */
  range: R
  /** The range the query uses: all time for a run id search. */
  effectiveRange: R | 'all'
  sort: SortState<F>
  /** 1-based. */
  page: number
  searchParams: URLSearchParams
  setSearch: (value: string) => void
  setRange: (value: R) => void
  setSort: (field: F) => void
  setPage: (next: number) => void
  setMetadata: (param: MetadataParam, value: string) => void
  clearMetadata: () => void
  /** One change of the listing's own params; resets the page like the rest. */
  updateParams: (mutate: (params: URLSearchParams) => void) => void
}

export function useListingParams<R extends string, F extends string>(
  config: ListingConfig<R, F>,
): ListingParams<R, F> {
  const [searchParams, setSearchParams] = useSearchParams()
  const range = parseRange(searchParams.get('range'), config.ranges, config.defaultRange)
  const sort = parseSort(searchParams.get('sort'), config.sortFields, config.defaultSort)
  const page = parsePage(searchParams.get('page'))
  const urlSearch = searchParams.get('q') ?? ''
  const [search, setSearch] = useState(urlSearch)
  // Adjust the box when `q` changes under it. State set during render, the
  // React way to follow a changed input without an effect's extra frame.
  const [seenUrlSearch, setSeenUrlSearch] = useState(urlSearch)
  if (urlSearch !== seenUrlSearch) {
    setSeenUrlSearch(urlSearch)
    setSearch(searchAfterUrlChange(urlSearch, search))
  }
  const debouncedSearch = useDebounce(search.trim(), 300)

  function updateParams(mutate: (params: URLSearchParams) => void): void {
    setSearchParams((prev) => changedParams(prev, mutate), { replace: true })
  }

  return {
    search,
    debouncedSearch,
    idSearch: isIdSearch(debouncedSearch),
    range,
    effectiveRange: rangeForSearch(debouncedSearch, range),
    sort,
    page,
    searchParams,
    updateParams,
    setSearch: (value) => {
      setSearch(value)
      updateParams((params) => {
        if (value.trim() === '') params.delete('q')
        else params.set('q', value.trim())
      })
    },
    setRange: (value) => {
      updateParams((params) => {
        if (value === config.defaultRange) params.delete('range')
        else params.set('range', value)
      })
    },
    setSort: (field) => {
      updateParams((params) => {
        const value = sortParamValue(nextSort(sort, field, config.defaultSort), config.defaultSort)
        if (value === null) params.delete('sort')
        else params.set('sort', value)
      })
    },
    setPage: (next) => {
      updateParams((params) => {
        if (next > 1) params.set('page', String(next))
      })
    },
    setMetadata: (param, value) => {
      updateParams((params) => {
        if (value === ANY) params.delete(param)
        else params.set(param, value)
      })
    },
    clearMetadata: () => {
      updateParams((params) => {
        for (const param of config.metadataParams) params.delete(param)
      })
    },
  }
}
